import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {PasteboxService} from '../../services/pastebox.service';
import {RaintreeResponse} from '../interface/raintree-response';
import {MessageService} from '../../services/message.service';
import {HttpErrorHandlerService} from '../http-error-handler.service';
import {JWTService} from 'src/app/authentication/jwt.service';
import axios, {AxiosResponse} from 'axios';
import {ConstantService} from "../../services/constant.service";

@Injectable({
	providedIn: 'root',
})
export class TransmissionService {
	constructor(
		private _http: HttpClient,
		private _httpErrorHandler: HttpErrorHandlerService,
		private _jwt: JWTService,
		private _pastebox: PasteboxService
	) {
	}

	public executeBasicGet(
		url: string,
		onPreExecute: () => void,
		onPostExecute: (response: string) => void,
		onSuccess: (response: string) => void,
		onFailure: (error: string) => void,
		onComplete: () => void
	): void {
		onPreExecute();

		axios.get<RaintreeResponse>(url, {
			headers: this._jwt.injectToken(url)
		})
			.then((response: AxiosResponse<RaintreeResponse>): void => {
				const data: RaintreeResponse = response.data;
				onPostExecute(data.message);
				if (data.success) {
					onSuccess(data.message);
				} else {
					onFailure(MessageService.RESOURCE_VALIDATION_FAILED);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
			})
			.finally((): void => {
				onComplete();
			});
	}

	public executeGetPayload<T extends RaintreeResponse, S>(
		url: string,
		onPreExecute: () => void,
		onPostExecute: (response: S) => void,
		onSuccess: (response: S) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		payloadKey: string,
		requiresAuthentication: boolean = true
	): void {
		onPreExecute();

		axios.get<T>(url, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<T, any>): void => {
				const responseData: T = response.data;
				onPostExecute(responseData[payloadKey]);

				if (responseData.success) {
					onSuccess(responseData[payloadKey]);
				} else {
					onFailure(MessageService.RESOURCE_VALIDATION_FAILED);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
			})
			.finally((): void => {
				onComplete();
			});

	}

	public executePostPayload<T>(
		url: string,
		payload: T,
		onPreExecute: () => void,
		onPostExecute: (response: RaintreeResponse) => void,
		onSuccess: (response: RaintreeResponse) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.post<RaintreeResponse>(url, payload, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<RaintreeResponse>) => {
				const data: RaintreeResponse = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					const errorMsg: string = this._pastebox.isEmptyString(data.message)
						? MessageService.RESOURCE_VALIDATION_FAILED
						: data.message;

					onFailure(errorMsg);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}

	public executePostPayloadCustomResponse<T>(
		url: string,
		payload: T,
		onPreExecute: () => void,
		onPostExecute: (response: RaintreeResponse) => void,
		onSuccess: (response: RaintreeResponse) => void,
		onFailure: (error: RaintreeResponse) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.post<RaintreeResponse>(url, payload, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<RaintreeResponse>): void => {
				const data: RaintreeResponse = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					onFailure(data);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}

	public executePatchPayload<T>(
		url: string,
		payload: T,
		onPreExecute: () => void,
		onPostExecute: (response: RaintreeResponse) => void,
		onSuccess: (response: RaintreeResponse) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.patch<RaintreeResponse>(url, payload, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<RaintreeResponse>): void => {
				const data: RaintreeResponse = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					const errorMsg: string = this._pastebox.isEmptyString(data.message)
						? MessageService.RESOURCE_VALIDATION_FAILED
						: data.message;
					onFailure(errorMsg);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}

	public executeDelete<T>(
		url: string,
		payload: T,
		onPreExecute: () => void,
		onPostExecute: (response: RaintreeResponse) => void,
		onSuccess: (response: RaintreeResponse) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.delete<RaintreeResponse>(url, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<RaintreeResponse>): void => {
				const data: RaintreeResponse = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					const errorMsg: string = this._pastebox.isEmptyString(data.message)
						? MessageService.RESOURCE_VALIDATION_FAILED
						: data.message;
					onFailure(errorMsg);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}


	public executeDeletePayload<T>(
		url: string,
		payload: T,
		onPreExecute: () => void,
		onPostExecute: (response: RaintreeResponse) => void,
		onSuccess: (response: RaintreeResponse) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.delete<RaintreeResponse>(url, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			},
			data: payload
		})
			.then((response: AxiosResponse<RaintreeResponse>): void => {
				const data: RaintreeResponse = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					const errorMsg: string = this._pastebox.isEmptyString(data.message)
						? MessageService.RESOURCE_VALIDATION_FAILED
						: data.message;
					onFailure(errorMsg);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}

	public executeFormPostPayload<T extends RaintreeResponse>(
		url: string,
		payload: FormData,
		onPreExecute: () => void,
		onPostExecute: (response: T) => void,
		onSuccess: (response: T) => void,
		onFailure: (error: string) => void,
		onComplete: () => void,
		requiresAuthentication: boolean = true,
	): void {

		onPreExecute();

		axios.post<T>(url, payload, {
			headers: requiresAuthentication ? this._jwt.injectToken(url) : {
				...(this._jwt._browser.isServer() ? { 'Origin': ConstantService.ORIGIN } : {})
			}
		})
			.then((response: AxiosResponse<T, any>): void => {
				const data: T = response.data;

				onPostExecute(data);

				if (data.success) {
					onSuccess(data);
				} else {
					const errorMsg: string = this._pastebox.isEmptyString(data.message)
						? MessageService.RESOURCE_VALIDATION_FAILED
						: data.message;
					onFailure(errorMsg);
				}
			})
			.catch((error): void => {
				this._httpErrorHandler.intercept(error);
				onFailure(error);
				onComplete();
			})
			.finally((): void => {
				onComplete();
			});
	}
}
